import React from 'react';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { GuideMap } from './GuideMap';
import { getLocationText } from '../i18n/locationText';

const Location = () => {
  const { hotelInfo, nearbyAttractions } = useHotel();
  const { lang } = useLanguage();
  const L = getLocationText(lang);
  
  return (
    <section id="location" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider" data-testid="location-kicker">{L.kicker}</p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900" data-testid="location-title">{L.title}</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {L.lead}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Map */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gray-200" style={{ height: '500px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1491.2!2d19.2342801!3d41.9100076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134e1500ed0c545b%3A0xea28060ed2fa2b9f!2sHotel%20La%20Rosa%20Negra!5e0!3m2!1sen!2sme!4v1707753600000!5m2!1sen!2sme"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Hotel La Rosa Negra Location"
            />
          </div>

          {/* Beaches, sights & airports at a glance (same interactive map as the Ulcinj guide) */}
          <div className="mt-8 location-guide-map" data-testid="location-guide-map">
            <GuideMap lang={lang} />
          </div>

          {/* Address and directions */}
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-orange-500" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Hotel La Rosa Negra</h3>
                  <p className="text-gray-600 mb-4">
                    {hotelInfo.address}<br />
                    {hotelInfo.postalCode} {hotelInfo.city}, {hotelInfo.country}
                  </p>
                  <div className="flex flex-col gap-2">
                    <a
                      href={hotelInfo.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold"
                    >
                      <Navigation size={18} />
                      {L.directions}
                    </a>
                    <a
                      href={hotelInfo.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold"
                    >
                      <ExternalLink size={18} />
                      {L.openMaps}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-gray-900" data-testid="location-getting-here">{L.gettingHere}</h3>
              <div className="space-y-4 text-gray-600">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold text-sm">✈️</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{L.podgorica}</p>
                    <p className="text-sm">{L.drives.podgorica} {L.drive}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold text-sm">✈️</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{L.tirana}</p>
                    <p className="text-sm">{L.drives.tirana} {L.drive}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold text-sm">✈️</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{L.dubrovnik}</p>
                    <p className="text-sm">{L.drives.dubrovnik} {L.drive}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-semibold text-orange-500">
                    ✈️ {L.transfer} — €80
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Nearby attractions */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-900" data-testid="location-nearby">{L.nearby}</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-lg text-gray-900">{attraction.name}</h4>
                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold flex-shrink-0">
                      {attraction.distance}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{attraction.description}</p>
                  {attraction.rating && (
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded text-sm font-semibold">
                        <span>★</span>
                        <span>{attraction.rating}</span>
                      </div>
                      <span className="text-xs text-gray-500">{L.topBeach}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;